import { useEffect, useMemo, useState } from 'react';

const TYPING_PHRASES_EN = [
  'uchch neech',
  'drishti',
  'vimshottari dasha',
  'nakshatra',
  'navamsa',
  'hora',
  'mool trikon',
  'ashtakvarga',
  'raja yoga',
  'neech bhanga',
  'gochar transit',
  'bhava lord',
];

const TYPING_PHRASES_HI = [
  'उच्च नीच',
  'दृष्टि',
  'विंशोत्तरी दशा',
  'नक्षत्र',
  'नवांश',
  'होरा',
  'मूल त्रिकोण',
  'अष्टकवर्ग',
  'राज योग',
  'नीच भंग',
  'गोचर',
  'भाव स्वामी',
];

export type UseTypingPlaceholderOptions = {
  enabled: boolean;
  showEnglish: boolean;
  restartKey?: number;
  typeMs?: number;
  deleteMs?: number;
  pauseMs?: number;
  phrasesEn?: readonly string[];
  phrasesHi?: readonly string[];
};

/** Cycles sample search phrases with a typewriter effect. */
export function useTypingPlaceholder({
  enabled,
  showEnglish,
  restartKey = 0,
  typeMs = 65,
  deleteMs = 35,
  pauseMs = 2000,
  phrasesEn = TYPING_PHRASES_EN,
  phrasesHi = TYPING_PHRASES_HI,
}: UseTypingPlaceholderOptions): string {
  const phrases = useMemo(
    () => (showEnglish ? [...phrasesEn] : [...phrasesHi]),
    [showEnglish, phrasesEn, phrasesHi],
  );
  const [text, setText] = useState('');

  useEffect(() => {
    if (!enabled || phrases.length === 0) {
      setText('');
      return undefined;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;

    const tick = () => {
      if (cancelled) return;
      const phrase = phrases[phraseIndex];

      if (!deleting) {
        charIndex += 1;
        setText(phrase.slice(0, charIndex));
        if (charIndex >= phrase.length) {
          deleting = true;
          timeoutId = setTimeout(tick, pauseMs);
        } else {
          timeoutId = setTimeout(tick, typeMs);
        }
        return;
      }

      charIndex -= 1;
      setText(phrase.slice(0, charIndex));
      if (charIndex <= 0) {
        deleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        timeoutId = setTimeout(tick, typeMs * 2);
      } else {
        timeoutId = setTimeout(tick, deleteMs);
      }
    };

    setText('');
    timeoutId = setTimeout(tick, 600);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enabled, phrases, typeMs, deleteMs, pauseMs, restartKey]);

  return text;
}
